import React, { useState } from 'react';
import { api, apiErrorMessage } from '../api/client';

/**
 * Florida SHOTS paper bridge.
 *
 * Schools hold view-only SHOTS access under a DH 2115 agreement and there is no
 * school-facing write API, so this screen does not integrate with the registry.
 * It produces the artefacts that make the nurse's manual entry fast and safe,
 * using the credential she already personally holds:
 *
 *  - a printable worksheet laid out in SHOTS data-entry order, and
 *  - a dose-level CSV for staff who prefer to work from a spreadsheet.
 *
 * Every export requires a typed business reason and is recorded in
 * phi_access_logs with its filters, row count and column set.
 */

const REASON_MIN = 8;

type Busy = null | 'transcription' | 'roster' | 'dh684';

function download(data: BlobPart, filename: string, mime: string) {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const ShotsBridgePage: React.FC = () => {
  const [reason, setReason] = useState('');
  const [gradeBand, setGradeBand] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reasonValid = reason.trim().length >= REASON_MIN;

  const run = async (
    kind: Exclude<Busy, null>,
    path: string,
    filename: string,
    params: Record<string, unknown>,
  ) => {
    setError(null);
    setMsg(null);
    if (!reasonValid) {
      setError(
        `A business reason of at least ${REASON_MIN} characters is required. It is recorded in the audit trail.`,
      );
      return;
    }
    setBusy(kind);
    try {
      const res = await api.get(path, {
        params: { reason, ...params },
        responseType: 'blob',
      });
      download(res.data as BlobPart, filename, 'text/csv');
      setMsg(`Downloaded ${filename}.`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Export failed.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          Florida SHOTS bridge
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Print or export what you need to key into Florida SHOTS with your own
          registry access. Nothing here is sent to the state.
        </p>
      </header>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong>Not an official immunization record.</strong> These documents are
        prepared from parent-submitted materials and school health staff review.
        The authoritative record is Florida SHOTS.
      </div>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <label
          htmlFor="reason"
          className="block text-sm font-medium text-slate-800"
        >
          Business reason for export <span className="text-red-600">*</span>
        </label>
        <p className="mb-2 text-xs text-slate-500">
          Recorded in the audit trail alongside the filters and row count.
        </p>
        <input
          id="reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Weekly SHOTS transcription batch — Gr 7 Tdap cohort"
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
        />
        {reason.length > 0 && !reasonValid && (
          <p className="mt-1 text-xs text-amber-700">
            At least {REASON_MIN} characters.
          </p>
        )}
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-800">Filters</h2>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="text-sm text-slate-700">
            Grade band{' '}
            <select
              value={gradeBand}
              onChange={(e) => setGradeBand(e.target.value)}
              className="ml-1 rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">All grades</option>
              <option value="K">Kindergarten</option>
              <option value="1-6">Grades 1–6</option>
              <option value="7-12">Grades 7–12</option>
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={onlyUnlinked}
              onChange={(e) => setOnlyUnlinked(e.target.checked)}
            />
            Only students without a State IMM ID
          </label>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Leaving the State IMM ID filter on keeps each batch to students who
          still need registry entry, so the list shrinks week over week.
        </p>
      </section>

      <section className="space-y-3">
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            SHOTS transcription CSV
          </h3>
          <p className="mt-1 text-xs text-slate-600">
            One row per dose, in registry order, with CVX codes. Includes{' '}
            <code>date_precision</code> so you can filter for the doses that need
            a call to the guardian before entry.
          </p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run(
                'transcription',
                '/api/admin/exports/transcription.csv',
                'shots-transcription.csv',
                { grade_band: gradeBand || undefined, only_unlinked: onlyUnlinked },
              )
            }
            className="mt-3 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy === 'transcription' ? 'Preparing…' : 'Download transcription CSV'}
          </button>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            Compliance roster CSV
          </h3>
          <p className="mt-1 text-xs text-slate-600">
            One row per student for the district SIS, including the FLDOE status
            code (1, 2, 3, 4, 8) so it does not have to be re-keyed.
          </p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run('roster', '/api/admin/exports/roster.csv', 'compliance-roster.csv', {
                grade_band: gradeBand || undefined,
              })
            }
            className="mt-3 rounded bg-slate-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy === 'roster' ? 'Preparing…' : 'Download roster CSV'}
          </button>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            DH 684 annual report
          </h3>
          <p className="mt-1 text-xs text-slate-600">
            Kindergarten and grade 7 cohort counts in the state's own buckets.
            Due to the county health department by October 1.
          </p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() =>
              run('dh684', '/api/admin/exports/dh684', 'dh684.csv', {
                format: 'csv',
              })
            }
            className="mt-3 rounded bg-slate-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy === 'dh684' ? 'Preparing…' : 'Download DH 684 CSV'}
          </button>
        </div>
      </section>

      {msg && (
        <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {msg}
        </p>
      )}
      {error && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}
    </div>
  );
};

export default ShotsBridgePage;
